import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Megaphone, ChevronDown, ChevronUp, Target, Users, DollarSign,
  Calendar, Tag, BarChart3, Edit, Trash2, FolderOpen, Layers, Settings,
  Link2, Copy, ExternalLink, Globe, MessageCircle, Zap, Smartphone, Smile
} from 'lucide-react';
import { WhatsAppCloudDialog } from '../components/WhatsAppCloudDialog';
import { TwilioDialog } from '../components/TwilioDialog';
import { DemoDataButton } from '../components/DemoDataButton';
import { CampaignStickersDialog } from '../components/CampaignStickersDialog';
import { CampaignWizard } from '../components/campaign/CampaignWizard';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const colorOptions = ['#002d2c', '#559975', '#77bd9b', '#e6fff1', '#f4eb9f', '#c48a4b', '#85562e', '#1a4034'];
const channelOptions = ['WhatsApp', 'Facebook', 'Instagram', 'TikTok', 'Telegram', 'Email', 'Web', 'Otro'];
const socialChannelOptions = [
  { value: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
  { value: 'messenger', label: 'Messenger', color: '#0084FF' },
  { value: 'instagram', label: 'Instagram', color: '#E4405F' },
  { value: 'tiktok', label: 'TikTok', color: '#000000' },
  { value: 'telegram', label: 'Telegram', color: '#0088CC' },
];

const Campaigns = () => {
  const navigate = useNavigate();
  const { campaigns, customers, campaignGroups, fetchCampaigns, updateCampaign, deleteCampaign, createCampaignGroup, updateCampaignGroup, deleteCampaignGroup } = useApp();
  const [waCampaign, setWaCampaign] = useState(null);
  const [smsCampaign, setSmsCampaign] = useState(null);
  const [stickerCampaign, setStickerCampaign] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [viewMode, setViewMode] = useState('groups'); // 'groups' or 'all'
  const [groupForm, setGroupForm] = useState({ name: '', description: '', color: '#6366F1', workflow: 'standard', campaign_ids: [] });

  const resetForm = () => setEditingCampaign(null);
  const handleEdit = (campaign) => { setEditingCampaign(campaign); setShowForm(true); };

  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) return;
    await createCampaignGroup(groupForm);
    setGroupForm({ name: '', description: '', color: '#6366F1', workflow: 'standard', campaign_ids: [] });
    setShowGroupForm(false);
    toast.success('Grupo creado');
  };


  const toggleChannel = (ch) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(ch) ? prev.channels.filter(c => c !== ch) : [...prev.channels, ch]
    }));
  };

  const getCampaignStats = (campaignId) => {
    const cc = customers.filter(c => c.campaign_id === campaignId);
    return { total: cc.length, won: cc.filter(c => c.status === 'won' || c.status === 'closed_won').length, totalValue: cc.reduce((s, c) => s + (c.value || 0), 0) };
  };

  const ungroupedCampaigns = campaigns.filter(c => !c.group_id);

  const CampaignCard = ({ campaign }) => {
    const stats = getCampaignStats(campaign.id);
    const isExpanded = expandedCampaign === campaign.id;
    const leadLink = campaign.lead_capture_code ? `${API}/api/lead/${campaign.lead_capture_code}` : null;
    const socialLabel = socialChannelOptions.find(s => s.value === campaign.social_channel);
    
    const copyLeadLink = (e) => {
      e.stopPropagation();
      if (leadLink) {
        navigator.clipboard.writeText(leadLink);
        toast.success('Link de captura copiado');
      }
    };

    return (
      <Card data-testid={`campaign-card-${campaign.id}`} className="glass-card overflow-hidden transition-all duration-300">
        <div className="h-1.5" style={{ backgroundColor: campaign.color }} />
        <CardContent className="p-0">
          <div className="p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}>
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${campaign.color}20` }}>
                  <Megaphone className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: campaign.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-base sm:text-lg truncate max-w-full">{campaign.name}</h3>
                    <Badge variant={campaign.active ? 'default' : 'secondary'} className="text-xs">{campaign.active ? 'Activa' : 'Pausada'}</Badge>
                    {socialLabel && (
                      <Badge variant="outline" className="text-xs gap-1" style={{ borderColor: socialLabel.color, color: socialLabel.color }}>
                        <Globe className="w-3 h-3" />
                        {socialLabel.label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{campaign.description || 'Sin descripción'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                <div className="hidden md:flex items-center gap-6 text-center">
                  <div><p className="text-lg font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Clientes</p></div>
                  <div><p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{stats.won}</p><p className="text-xs text-muted-foreground">Ganados</p></div>
                  <div><p className="text-lg font-bold">${stats.totalValue.toLocaleString()}</p><p className="text-xs text-muted-foreground">Valor</p></div>
                </div>
                <Button variant="outline" size="sm" title="Configurar el número de WhatsApp de esta campaña"
                  data-testid={`campaign-whatsapp-quick-${campaign.id}`}
                  onClick={(e) => { e.stopPropagation(); setWaCampaign(campaign); }}
                  className="border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10">
                  <MessageCircle className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">WhatsApp</span>
                </Button>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
              </div>
            </div>
          </div>
          {isExpanded && (
            <div className="border-t border-border p-4 space-y-4 animate-expand-in">
              {/* Lead Capture Link */}
              {leadLink && (
                <div className="p-3 bg-primary/10 rounded-lg border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-700 dark:text-emerald-400">Link de Captura de Leads</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-background/60 p-2 rounded border border-border truncate" data-testid={`lead-link-${campaign.id}`}>
                      {leadLink}
                    </code>
                    <Button variant="outline" size="sm" onClick={copyLeadLink} data-testid={`copy-lead-link-${campaign.id}`}>
                      <Copy className="w-3 h-3 mr-1" /> Copiar
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Comparte este link en {socialLabel?.label || 'redes sociales'}. Los clientes que escriban entrarán como leads y Noah IA comenzará la venta.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {campaign.objective && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><Target className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" /><span className="text-xs font-medium text-muted-foreground">Objetivo</span></div><p className="text-sm">{campaign.objective}</p></div>)}
                {campaign.target_audience && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-brand-deep dark:text-brand-green" /><span className="text-xs font-medium text-muted-foreground">Audiencia</span></div><p className="text-sm">{campaign.target_audience}</p></div>)}
                {campaign.budget && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-emerald-700 dark:text-emerald-400" /><span className="text-xs font-medium text-muted-foreground">Presupuesto</span></div><p className="text-sm font-semibold">${Number(campaign.budget).toLocaleString()}</p></div>)}
                {(campaign.start_date || campaign.end_date) && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-amber-700 dark:text-amber-400" /><span className="text-xs font-medium text-muted-foreground">Periodo</span></div><p className="text-sm">{campaign.start_date || '?'} — {campaign.end_date || '?'}</p></div>)}
                {campaign.kpis && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><BarChart3 className="w-4 h-4 text-brand-deep dark:text-brand-green" /><span className="text-xs font-medium text-muted-foreground">KPIs</span></div><p className="text-sm">{campaign.kpis}</p></div>)}
                {campaign.notes && (<div className="p-3 bg-muted/30 rounded-lg"><div className="flex items-center gap-2 mb-1"><Tag className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" /><span className="text-xs font-medium text-muted-foreground">Notas</span></div><p className="text-sm">{campaign.notes}</p></div>)}
              </div>
              {campaign.channels?.length > 0 && (<div><p className="text-xs font-medium text-muted-foreground mb-2">Canales</p><div className="flex flex-wrap gap-1.5">{campaign.channels.map(ch => <Badge key={ch} variant="outline" className="text-xs">{ch}</Badge>)}</div></div>)}
              {campaign.tags?.length > 0 && (<div><p className="text-xs font-medium text-muted-foreground mb-2">Etiquetas</p><div className="flex flex-wrap gap-1.5">{campaign.tags.map(tag => <Badge key={tag} className="text-xs" style={{ backgroundColor: `${campaign.color}20`, color: campaign.color }}>#{tag}</Badge>)}</div></div>)}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Clientes ({customers.filter(c => c.campaign_id === campaign.id).length})</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {customers.filter(c => c.campaign_id === campaign.id).slice(0, 8).map(cust => (
                    <div key={cust.id} className="flex items-center justify-between p-2 bg-muted/20 rounded-lg text-sm">
                      <span>{cust.name}</span>
                      <div className="flex items-center gap-2">
                        {cust.value > 0 && <span className="text-xs text-emerald-700 dark:text-emerald-400">${cust.value}</span>}
                        <Badge variant="outline" className="text-xs">{cust.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(campaign)}><Edit className="w-4 h-4 mr-1" /> Editar</Button>
                <Button variant="outline" size="sm" data-testid={`campaign-whatsapp-${campaign.id}`}
                  onClick={() => setWaCampaign(campaign)}
                  className="border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10">
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Cloud
                </Button>
                <Button variant="outline" size="sm" data-testid={`campaign-stickers-${campaign.id}`}
                  onClick={() => setStickerCampaign(campaign)}
                  className="border-brand-butter/60 text-brand-copper hover:bg-brand-butter/20">
                  <Smile className="w-4 h-4 mr-1" /> Stickers
                </Button>
                <DemoDataButton campaign={campaign} />
                <Button variant="outline" size="sm" data-testid={`campaign-sms-${campaign.id}`}
                  onClick={() => setSmsCampaign(campaign)}
                  className="border-brand-copper/40 text-brand-copper hover:bg-brand-copper/10">
                  <Smartphone className="w-4 h-4 mr-1" /> SMS
                </Button>
                <Button variant="outline" size="sm" data-testid={`campaign-automations-${campaign.id}`}
                  onClick={() => navigate('/automations')}
                  className="border-violet-500/40 text-brand-deep dark:text-brand-deep dark:text-brand-green hover:bg-violet-500/10">
                  <Zap className="w-4 h-4 mr-1" /> Automatizaciones
                </Button>
                <Button variant="outline" size="sm" data-testid={`campaign-report-${campaign.id}`}
                  onClick={() => navigate('/reports')}
                  className="border-brand-green/40 text-brand-deep dark:text-brand-green hover:bg-brand-green/10">
                  <BarChart3 className="w-4 h-4 mr-1" /> Reporte
                </Button>
                <Button variant="destructive" size="sm" onClick={() => { deleteCampaign(campaign.id); toast.success('Campaña eliminada'); }}><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="campaigns-page" className="min-h-screen">
      <Header title="Campañas" subtitle="Gestiona campañas y grupos de trabajo">
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button variant={viewMode === 'groups' ? 'secondary' : 'ghost'} size="sm" className="text-xs h-7" onClick={() => setViewMode('groups')}>
              <Layers className="w-3 h-3 mr-1" /> Grupos
            </Button>
            <Button variant={viewMode === 'all' ? 'secondary' : 'ghost'} size="sm" className="text-xs h-7" onClick={() => setViewMode('all')}>
              <Megaphone className="w-3 h-3 mr-1" /> Todas
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowGroupForm(true)} data-testid="new-group-btn">
            <FolderOpen className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Nuevo Grupo</span>
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="gap-2 bg-primary hover:opacity-90" data-testid="new-campaign-btn" aria-label="Nueva Campaña" title="Nueva Campaña">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nueva Campaña</span>
          </Button>
        </div>
      </Header>

      <div className="p-6 space-y-6">
        {viewMode === 'groups' ? (
          <>
            {/* Campaign Groups */}
            {campaignGroups.map(group => {
              const groupCampaigns = campaigns.filter(c => c.group_id === group.id);
              const isExpanded = expandedGroup === group.id;
              const totalClients = groupCampaigns.reduce((s, c) => s + customers.filter(cu => cu.campaign_id === c.id).length, 0);
              
              return (
                <div key={group.id} data-testid={`group-${group.id}`}>
                  <Card className="glass-card overflow-hidden">
                    <div className="h-2" style={{ backgroundColor: group.color }} />
                    <div className="p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpandedGroup(isExpanded ? null : group.id)}>
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${group.color}20` }}>
                            <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: group.color }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h2 className="text-base sm:text-lg font-bold truncate max-w-full">{group.name}</h2>
                              <Badge variant="outline" className="text-xs">{groupCampaigns.length} campañas</Badge>
                              {group.workflow && <Badge variant="secondary" className="text-xs capitalize">{group.workflow}</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{group.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-4 shrink-0">
                          <div className="text-right hidden md:block">
                            <p className="text-lg font-bold">{totalClients}</p>
                            <p className="text-xs text-muted-foreground">clientes total</p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteCampaignGroup(group.id); toast.success('Grupo eliminado'); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                  </Card>
                  
                  {isExpanded && (
                    <div className="ml-2 sm:ml-6 mt-2 space-y-3 animate-expand-in">
                      {groupCampaigns.map(camp => <CampaignCard key={camp.id} campaign={camp} />)}
                      {groupCampaigns.length === 0 && (
                        <p className="text-center text-sm text-muted-foreground py-4">Sin campañas en este grupo</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ungrouped campaigns */}
            {ungroupedCampaigns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Sin grupo</h3>
                <div className="space-y-3">
                  {ungroupedCampaigns.map(camp => <CampaignCard key={camp.id} campaign={camp} />)}
                </div>
              </div>
            )}
          </>
        ) : (
          /* All campaigns flat */
          <div className="space-y-3">
            {campaigns.map(camp => <CampaignCard key={camp.id} campaign={camp} />)}
          </div>
        )}

        {campaigns.length === 0 && (
          <Card className="glass-card border-dashed">
            <CardContent className="py-12 text-center">
              <Megaphone className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">Sin campañas</h3>
              <p className="text-muted-foreground mb-4">Crea tu primera campaña para organizar tus ventas</p>
              <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary">
                <Plus className="w-4 h-4 mr-2" /> Crear Campaña
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <CampaignWizard
        open={showForm}
        campaign={editingCampaign}
        groups={campaignGroups}
        onClose={() => { resetForm(); setShowForm(false); }}
        onSaved={fetchCampaigns}
      />

      {/* Group Form Dialog */}
      <Dialog open={showGroupForm} onOpenChange={setShowGroupForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuevo Grupo de Campañas</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nombre del Grupo *</Label><Input data-testid="group-name-input" value={groupForm.name} onChange={(e) => setGroupForm({...groupForm, name: e.target.value})} placeholder="Ej: Ventas Q1" /></div>
            <div><Label>Descripción</Label><Textarea value={groupForm.description} onChange={(e) => setGroupForm({...groupForm, description: e.target.value})} placeholder="Describe el propósito del grupo..." rows={2} /></div>
            <div><Label>Método de Trabajo</Label>
              <Select value={groupForm.workflow} onValueChange={v => setGroupForm({...groupForm, workflow: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Estándar</SelectItem>
                  <SelectItem value="express">Express</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                  <SelectItem value="automated">Automatizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Color</Label><div className="flex gap-2 mt-1">{colorOptions.map(c => <button key={c} className={cn("w-8 h-8 rounded-full border-2", groupForm.color === c ? "border-foreground scale-110" : "border-transparent")} style={{ backgroundColor: c }} onClick={() => setGroupForm({...groupForm, color: c})} />)}</div></div>
            <div><Label>Campañas del grupo</Label>
              <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                {campaigns.map(camp => (
                  <label key={camp.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/30 cursor-pointer">
                    <input type="checkbox" checked={groupForm.campaign_ids.includes(camp.id)} onChange={(e) => {
                      setGroupForm(prev => ({ ...prev, campaign_ids: e.target.checked ? [...prev.campaign_ids, camp.id] : prev.campaign_ids.filter(id => id !== camp.id) }));
                    }} className="rounded" />
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: camp.color }} />
                    <span className="text-sm">{camp.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="save-group-btn" className="w-full bg-primary" onClick={handleCreateGroup} disabled={!groupForm.name.trim()}>Crear Grupo</Button>
          </div>
        </DialogContent>
      </Dialog>

      <WhatsAppCloudDialog
        campaign={waCampaign}
        open={!!waCampaign}
        onOpenChange={(o) => !o && setWaCampaign(null)}
      />

      <CampaignStickersDialog
        campaign={stickerCampaign}
        open={!!stickerCampaign}
        onOpenChange={(o) => !o && setStickerCampaign(null)}
      />

      <TwilioDialog
        campaign={smsCampaign}
        open={!!smsCampaign}
        onOpenChange={(o) => !o && setSmsCampaign(null)}
      />
    </div>
  );
};

export default Campaigns;
